import React, { useContext, useEffect, useState } from 'react';
import { Panel, PanelType, PrimaryButton, DefaultButton, MessageBar, MessageBarType } from '@fluentui/react';
import { Editor } from '@toast-ui/react-editor';
import '@toast-ui/editor/dist/toastui-editor.css';
import { ApplicationContext } from '../contexts/ApplicationContext';
import { IPersonalNote } from '../models';
import LoadingIndicator from './LoadingIndicator';
import styles from '../styles/App.module.scss';
interface IPersonalNotesPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
}

const PersonalNotesPanel: React.FunctionComponent<IPersonalNotesPanelProps> = (props) => {
    const { Services } = useContext(ApplicationContext);
    const [note, setNote] = useState<IPersonalNote>({ content: '' });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [originalContent, setOriginalContent] = useState<string>('');
    const [editorInstance, setEditorInstance] = useState<any>(null);
    const [isExistingNote, setIsExistingNote] = useState<boolean>(false);

    useEffect(() => {
        if (props.isOpen) {
            loadNote();
        }
    }, [props.isOpen]);

    // Update editor content when note is loaded and editor is ready
    useEffect(() => {
        if (editorInstance && note.content !== editorInstance.getMarkdown()) {
            editorInstance.setMarkdown(note.content);
        }
    }, [editorInstance, note.content]);

    const loadNote = async () => {
        setIsLoading(true);
        setMessage('');
        try {
            const userNote = await Services?.PersonalNote.GetUserNote();
            if (userNote) {
                setNote(userNote);
                setOriginalContent(userNote.content);
                setIsExistingNote(true);
            } else {
                // No note exists yet, start with empty
                const emptyNote: IPersonalNote = { content: '' };
                setNote(emptyNote);
                setOriginalContent('');
                setIsExistingNote(false);
            }
            setHasUnsavedChanges(false);
        } catch (error) {
            setMessage('Der opstod en fejl ved indlæsning af dine noter.');
        } finally {
            setIsLoading(false);
        }
    };

    const saveNote = async () => {
        setIsSaving(true);
        setMessage('');
        try {
            let result: string | undefined;
            
            if (isExistingNote) {
                // Update existing note using PATCH
                result = await Services?.PersonalNote.UpdateUserNote(note);
            } else {
                // Create new note using POST
                result = await Services?.PersonalNote.CreateUserNote(note);
            }
            
            if (result === 'OK') {
                setMessage('Dine noter er blevet gemt');
                setOriginalContent(note.content);
                setHasUnsavedChanges(false);
                
                // After successful creation, mark as existing for future updates
                if (!isExistingNote) {
                    setIsExistingNote(true);
                }
            } else {
                setMessage('Der opstod en fejl ved gemning af dine noter.');
            }
        } catch (error) {
            setMessage('Der opstod en fejl ved gemning af dine noter.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleContentChange = () => {
        if (editorInstance) {
            const content = editorInstance.getMarkdown();
            setNote({ ...note, content });
            setHasUnsavedChanges(content !== originalContent);
            setMessage('');
        }
    };

    const handleDismiss = () => {
        if (hasUnsavedChanges) {
            if (window.confirm('Du har ikke-gemte ændringer. Vil du lukke uden at gemme?')) {
                props.onDismiss();
            }
        } else {
            props.onDismiss();
        }
    };

    const onRenderFooterContent = () => {
        return (
            <>
                <PrimaryButton 
                    disabled={isSaving || !hasUnsavedChanges}
                    onClick={saveNote}
                    text={isSaving ? 'Gemmer...' : 'Gem'}
                    styles={{ root: { marginRight: '10px' } }}
                />
                <DefaultButton 
                    onClick={handleDismiss}
                    text="Luk"
                />
            </>
        );
    };

    return (
        <Panel 
            isOpen={props.isOpen}
            onDismiss={handleDismiss}
            headerText="Mine personlige noter"
            type={PanelType.custom}
            customWidth='60vw'
            isLightDismiss={false}
            isBlocking={true}
            onRenderFooterContent={onRenderFooterContent}
            closeButtonAriaLabel="Luk panel"
        >
            {isLoading ? (
                <div>
                    <LoadingIndicator size={48} label="Indlæser noter..." />
                </div>
            ) : (
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {message && (
                        <MessageBar 
                            messageBarType={message.includes('fejl') ? MessageBarType.error : MessageBarType.success}
                            isMultiline={false}
                            dismissButtonAriaLabel="Luk"
                            onDismiss={() => setMessage('')}
                            styles={{ root: { marginBottom: '10px' } }}
                        >
                            {message}
                        </MessageBar>
                    )}
                    
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Editor
                                ref={(ref) => setEditorInstance(ref?.getInstance())}
                                initialValue={note.content}                                
                                height="80vh"
                                initialEditType="wysiwyg"
                                hideModeSwitch={true}
                                toolbarItems={[
                                    ['heading', 'bold', 'italic'],
                                    ['ul', 'ol'],
                                    ['hr']
                                ]}
                                onChange={handleContentChange}
                                placeholder="Skriv dine personlige noter her..."
                            />
                        </div>
                        
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                            {hasUnsavedChanges && (
                                <div style={{ color: '#ff8c00', marginTop: '4px' }}>
                                    ⚠ Du har ikke-gemte ændringer
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </Panel>
    );
};

export default PersonalNotesPanel;